import {
  adminSectionTableFontSize,
  minimalWidthToFitContent,
  SessionTimeCell,
  DateCell
} from '@efacity/frontend-shared';
import { LightTooltip } from '@efacity/react-next-sc';
import {
  ActivityTypes,
  convertUtcToZonedTime,
  DurationType,
  getSessionDuration,
  getStudentLevelDiapason,
  moneyWithCurrencyCodeFormatter,
  nowUTC,
  SessionStatus,
  formatStringToMonthDayYearDate
} from '@efacity/common';
import { Session as EfacitySession } from '@efacity/react-next-sc';
import { PATHS, toPath } from '@efacity/routing';
import Lock from '@mui/icons-material/Lock';
import { Box, Link, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import SessionLocationCell from '../../components/SessionLocationCell/SessionLocationCell';
import SessionRegistrationLinkWithCopier from '../../components/SessionRegistrationLinkWithCopier/SessionRegistrationLinkWithCopier';
import SessionsTableActionsCell from './SessionsTableActionsCell';
import { TextColumnFilter, LocationTypeFilter, createTableColumnHelper } from '@efacity/table';

export interface SessionActions {
  onCloneSession(session: EfacitySession): void;
  handleRestoreSession(sessionId: string): void;
  onShowCancellationModal(sessionId: string, setSessionRowUnderUpdate?: any): void;
  onSessionStatusChanged(sessionId: string, newStatus: SessionStatus): void;
}

const displaySessionDuration = (session: EfacitySession) => {
  if (session.durationType === DurationType.selfPaced) return 'self-paced';
  if (!session?.schedule?.instances) return 'N/A';
  return getSessionDuration(session.schedule.instances.length, session.type);
};

const displaySessionCapacity = (session: EfacitySession): [string, string] => {
  if (session.durationType === DurationType.selfPaced) return [`${session.registrationsNumber}`, 'primary'];

  const registrationsAmount = session.registrationsNumber;
  const capacity = session.capacity;

  const textColor = registrationsAmount >= capacity ? 'error' : 'primary';
  return [`${registrationsAmount} / ${capacity}`, textColor];
};

const columnHelper = createTableColumnHelper<EfacitySession>();

const useSessionsTableColumns = (
  accessId: string,
  isUserAllowedToPerformSessionActions: boolean,
  isUserIndependentTeacher: boolean,
  sessionActions: SessionActions,
  width: string,
  timeHeader: string,
  sessionIdUnderUpdate: string | null
) => {
  const columns = useMemo(() => {
    const sessionNameColumn = columnHelper.accessor('name', {
      header: 'Name',
      enableColumnFilter: true,
      enableSorting: true,
      cell: ({ row }) => {
        return !row.original?.cancelledAt ? (
          <Box display="flex" alignItems="center" data-testid="session-name-copier-cell" style={{ marginRight: 5 }}>
            {row.original.status === SessionStatus.Private ? (
              <LightTooltip
                title={
                  <Typography>
                    Session is private and not visible to the public. To make it public click "Switch to public"
                  </Typography>
                }
              >
                <span>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{ display: 'table', marginRight: 5 }}>
                      <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
                        <Lock color="primary" />
                      </div>
                    </div>
                    <div>
                      <div style={{ display: 'table' }}>
                        <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
                          <Link
                            component={NavLink}
                            to={toPath(PATHS.session, { orgId: accessId, id: row.original._id })}
                            underline={'none'}
                          >
                            {row.original.name} {row.original.section && <span>({row.original.section})</span>}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </span>
              </LightTooltip>
            ) : (
              <Link
                component={NavLink}
                to={toPath(PATHS.session, { orgId: accessId, id: row.original._id })}
                underline={'none'}
              >
                {row.original.name} {row.original.section && <span>({row.original.section})</span>}
              </Link>
            )}
            &nbsp;
            <SessionRegistrationLinkWithCopier
              isEnabled={true}
              sessionId={row.original._id}
              owner={row.original.owner}
              type={row.original.type as ActivityTypes}
              activityId={row.original.activityId}
              locationId={row.original?.location?._id}
            />
          </Box>
        ) : (
          <Box data-testid="session-name-copier-cell" style={{ marginRight: 5 }}>
            <div style={{ color: 'red' }}>(cancelled: '{row.original.cancellationReason}')</div>
            {row.original.name} {row.original.section && <span>({row.original.section})</span>}
          </Box>
        );
      },
      meta: {
        filter: (column) => <TextColumnFilter column={column} />,
        headerSx: { minWidth: '170px' },
        columnSx: { minWidth: '170px' }
      }
    });

    const sessionGradeColumn = {
      header: 'Grade',
      id: 'session-grade',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        return (
          <div data-testid="grade">
            {row.original?.studentLevel ? getStudentLevelDiapason(row.original.studentLevel) : '-'}
          </div>
        );
      },
      meta: {
        columnSx: { verticalAlign: 'top' }
      }
    };

    const timeDateColumn = {
      header: timeHeader,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div data-testid="session-time-cell">
          {row.original?.schedule ? (
            <SessionTimeCell
              sessionSchedule={row.original.schedule}
              timeZone={row.original.owner.orgId.timeZone}
              displayTimeZone={false}
            />
          ) : (
            'N / A'
          )}
        </div>
      ),
      meta: {
        headerSx: { whiteSpace: 'pre-wrap' },
        columnSx: { verticalAlign: 'top' }
      }
    };
    const startDateColumn = {
      header: 'Starts On',
      id: 'starts-on',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div data-testid="session-start-date-cell">
          <DateCell
            dateTime={
              row.original?.schedule?.startDateTime
                ? convertUtcToZonedTime(row.original?.schedule?.startDateTime, row.original.owner.orgId.timeZone)
                : undefined
            }
            showTime={false}
            wrap={true}
            style={{ ...adminSectionTableFontSize }}
          />
        </div>
      ),
      meta: {
        columnSx: { verticalAlign: 'top' }
      }
    };
    const endDateColumn = {
      header: 'Ends On',
      id: 'end-date',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div data-testid="session-end-date-cell">
          <DateCell
            dateTime={
              row.original?.schedule?.endDateTime
                ? convertUtcToZonedTime(row.original?.schedule?.endDateTime, row.original.owner.orgId.timeZone)
                : undefined
            }
            showTime={false}
            style={{ ...adminSectionTableFontSize }}
          />
        </div>
      ),
      meta: {
        columnSx: { verticalAlign: 'top' }
      }
    };
    const daySColumn = {
      header: 'Duration',
      id: 'days-amount',
      accessor: 'duration',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => <div data-testid="session-days-cell">{displaySessionDuration(row.original)}</div>,
      meta: {
        columnSx: { verticalAlign: 'top' }
      }
    };
    const priceColumn = {
      header: 'Price',
      id: 'formatted-price',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }} data-testid="session-price-cell">
          {moneyWithCurrencyCodeFormatter(row.original.price, row.original.currency)}
        </div>
      ),
      meta: {
        headerSx: { textAlign: 'right' },
        columnSx: { verticalAlign: 'top', textAlign: 'right' }
      }
    };
    const instructorColumn = {
      header: 'Instructor',
      id: 'instructor',
      enableSorting: false,
      enableColumnFilter: false,
      columnStyles: { ...adminSectionTableFontSize, ...minimalWidthToFitContent },
      cell: ({ row }) => (
        <div data-testid="session-teacher-cell">{row.original?.teacher ? row.original.teacher.fullName : 'N/A'}</div>
      ),
      meta: {
        columnSx: { ...minimalWidthToFitContent, verticalAlign: 'top' }
      }
    };
    const registrationDeadlineColumn = {
      header: 'Registration Deadline',
      accessor: 'schedule.deadlineForCustomerRegistration',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ value, row }) => {
        const dataTestId = 'session-registration-deadline-cell';
        if (!value) return <div data-testid={dataTestId}>N/A</div>;

        const deadlineDate = convertUtcToZonedTime(value, row.original.owner.orgId.timeZone);
        const convertToTimeZone = deadlineDate.toISOString();

        const isExpired = new Date(value) < nowUTC();
        const divStyle = { color: isExpired ? '#FF0100' : 'initial', width: '7ch' };
        return (
          <div style={divStyle} data-testid={dataTestId}>
            {formatStringToMonthDayYearDate(convertToTimeZone)}
          </div>
        );
      },
      meta: {
        headerSx: { whiteSpace: 'normal' },
        columnSx: { ...minimalWidthToFitContent, verticalAlign: 'top' }
      }
    };
    const capacityColumn = {
      header: 'Students',
      id: 'capacity',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        const [displayValue, textColor] = displaySessionCapacity(row.original);
        return (
          <Typography style={adminSectionTableFontSize}>
            <Link
              component={NavLink}
              underline={'none'}
              to={`${toPath(PATHS.session, { orgId: accessId, id: row.original._id })}/students`}
              color={textColor}
            >
              {displayValue}
            </Link>
          </Typography>
        );
      },
      meta: {
        columnSx: { verticalAlign: 'top' }
      }
    };
    const locationColumn = columnHelper.accessor('location.type', {
      header: 'Location',
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => (
        <div data-testid="session-location-cell">
          <SessionLocationCell session={row.original} />
        </div>
      ),
      meta: {
        filter: (column) => <LocationTypeFilter column={column} />,
        columnSx: { verticalAlign: 'top' }
      }
    });

    let columnsBase;
    if (width === 'md') {
      columnsBase = [
        sessionNameColumn,
        sessionGradeColumn,
        timeDateColumn,
        startDateColumn,
        endDateColumn,
        daySColumn,
        priceColumn,
        ...(isUserIndependentTeacher ? [] : [instructorColumn]),
        registrationDeadlineColumn,
        capacityColumn,
        locationColumn
      ];
    } else if (width === 'sm') {
      columnsBase = [sessionNameColumn, startDateColumn, endDateColumn, daySColumn, priceColumn, locationColumn];
    } else {
      columnsBase = [sessionNameColumn, startDateColumn, priceColumn];
    }

    if (isUserAllowedToPerformSessionActions) {
      return [
        ...columnsBase,
        {
          header: '',
          id: 'session-actions',
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }) => {
            return (
              <SessionsTableActionsCell
                session={row.original}
                sessionIdUnderUpdate={sessionIdUnderUpdate}
                {...sessionActions}
              />
            );
          },
          meta: {
            headerSx: { width: '195px', maxWidth: '195px' },
            columnSx: { width: '195px', maxWidth: '195px', verticalAlign: 'top' }
          }
        }
      ];
    }

    return columnsBase;
  }, [
    accessId,
    sessionActions,
    isUserAllowedToPerformSessionActions,
    width,
    timeHeader,
    isUserIndependentTeacher,
    sessionIdUnderUpdate
  ]);

  return columns;
};

export default useSessionsTableColumns;
